/**
 * Shared presentational chips for the operator `tags` carried by a served
 * decision (`FinalDecisionCompact.tags`).
 *
 * Tags are free-form strings written server-side through
 * `PATCH /v1/audit-records/{audit_id}`; the console only RENDERS them. The
 * label map below is the whole extension point: adding a future tag is one
 * entry here and it shows up in the list rows and the detail banner at once.
 * An unknown tag renders nothing — the console never invents a label for a
 * code it does not know.
 */

import { Chip, Stack } from "@mui/material";

/** A known tag's chip: its Spanish label and the MUI palette it renders in. */
export interface TagChipSpec {
  label: string;
  color: "info" | "warning";
}

/** Known tag codes → their chip spec. Unknown codes are not rendered. */
export const TAG_LABEL: Record<string, TagChipSpec> = {
  menor_de_edad: { label: "Menor de edad", color: "info" },
  // Resultado de un re-scan QR determinista fuera de ronda (PRD #305).
  // «Dominio sospechoso» sale en warning para que el revisor lo priorice —
  // sigue siendo advisory, jamás un veredicto. El tag qr_rescan_limpio existe
  // en los datos pero NO se renderiza a propósito: un chip «QR verificado»
  // sugeriría una verificación autoritativa que Vera no hace (trust model).
  qr_rescan_ilegible: { label: "QR ilegible", color: "info" },
  qr_dominio_sospechoso: { label: "QR: dominio sospechoso", color: "warning" },
};

/** The known tags of a row, in the order the server sent them, de-duplicated. */
export function knownTagSpecs(tags: readonly string[] | undefined): TagChipSpec[] {
  if (!tags?.length) return [];
  const seen = new Set<string>();
  const specs: TagChipSpec[] = [];
  for (const tag of tags) {
    const spec = TAG_LABEL[tag];
    if (spec && !seen.has(tag)) {
      seen.add(tag);
      specs.push(spec);
    }
  }
  return specs;
}

/** Back-compat helper: just the labels (tests and callers that only need text). */
export function knownTagLabels(tags: readonly string[] | undefined): string[] {
  return knownTagSpecs(tags).map((s) => s.label);
}

/**
 * The operator-tag chips for a decision. Renders nothing when the row carries
 * no tags the console knows about (the common case), so it costs no layout.
 */
export function TagChips({
  tags,
  size = "small",
}: {
  tags: readonly string[] | undefined;
  size?: "small" | "medium";
}) {
  const specs = knownTagSpecs(tags);
  if (!specs.length) return null;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {specs.map(({ label, color }) => (
        <Chip
          key={label}
          label={label}
          size={size}
          color={color}
          variant="outlined"
        />
      ))}
    </Stack>
  );
}
